import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { useAuth } from '../lib/auth.js'
import { Button } from '../components/Button.js'
import { Icon } from '../components/Icon.js'

const verifyEmailSearchSchema = z.object({
  // Passed from /signup so we can show the address and offer a resend.
  email: z.string().email().optional(),
})

export const Route = createFileRoute('/_auth/verify-email')({
  validateSearch: verifyEmailSearchSchema,
  component: VerifyEmailPage,
})

function VerifyEmailPage() {
  const { email } = useSearch({ from: '/_auth/verify-email' })
  const { resendVerification } = useAuth()
  const [resending, setResending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onResend = async () => {
    if (!email) return
    setError(null)
    setResending(true)
    try {
      await resendVerification(email)
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the email')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-fg">Check your email</h1>
        <p className="text-sm text-fg-muted">
          {email ? (
            <>
              We sent a verification link to{' '}
              <strong className="text-fg">{email}</strong>. Click it to activate
              your account and continue setting up your academy.
            </>
          ) : (
            <>
              We sent you a verification link. Click it to activate your account
              and continue setting up your academy.
            </>
          )}
        </p>
      </header>

      <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-fg-muted">
        <Icon name="alert" size={14} className="mt-0.5 text-fg-subtle" />
        <p>
          Can&apos;t find it? Check your spam folder. The link expires after a
          while, so resend if it has been a few minutes.
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
          {error}
        </div>
      ) : null}

      {sent ? (
        <div className="rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
          Sent. Check your inbox again.
        </div>
      ) : null}

      {email ? (
        <Button
          onClick={onResend}
          loading={resending}
          variant="secondary"
          className="w-full"
        >
          Resend verification email
        </Button>
      ) : null}

      <p className="text-center text-sm text-fg-muted">
        Already verified?{' '}
        <Link to="/login" className="font-medium text-[var(--color-primary)] hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  )
}
